import { useEffect, useState } from 'react';
import { Drawer, Descriptions, Tag, Avatar, Space, Typography, type TagTone } from '../../ui';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../lib/mapTiles';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Phone, MessageCircle, MapPin, User, ExternalLink } from 'lucide-react';
import { STATE_NAME } from '../../constants/indianStates';
import { formatIstDate } from '../../lib/time';

// Self-contained SVG pin (Leaflet's default marker images don't resolve under bundlers).
const PIN = L.divIcon({
  className: '', iconSize: [28, 28], iconAnchor: [14, 28],
  html: `<svg viewBox="0 0 24 32" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="#1F8A4C" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white"/></svg>`,
});

// Leaflet mis-measures inside the sliding Drawer — recompute once it has settled.
function FixSize() {
  const map = useMap();
  useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 260); return () => clearTimeout(t); }, [map]);
  return null;
}

const STATUS_COLOR: Record<string, TagTone> = { ACTIVE: 'green', INACTIVE: 'red' };

// Small "default" action button rendered as an anchor — link buttons must navigate via href,
// and the ui Button is a <button> (no href), so anchors preserve the tel:/wa.me/maps navigation.
const ACTION_BTN = 'inline-flex h-8 items-center justify-center gap-1.5 rounded-control border border-line bg-surface px-3 text-[13px] font-semibold leading-none text-content transition-colors duration-150 hover:border-brand-400 hover:text-brand-700 dark:hover:border-brand-500';

/** Read-only farmer profile in a slide-over — full details + an embedded map. Works for any viewer with
 *  FARMER.VIEW (no edit rights needed). Built from the list row (the list returns full entities). */
export function FarmerDetailDrawer({ farmer, open, onClose }: { farmer: any; open: boolean; onClose: () => void }) {
  // Only mount the Leaflet map AFTER the drawer's slide-in finishes, so it measures a full-size container
  // and actually loads tiles (mounting mid-animation leaves the tiles blank — the pin renders but no map).
  const [settled, setSettled] = useState(false);
  // ui Drawer has no afterOpenChange, so replicate the antd behaviour: flip `settled` true only once the
  // slide-in has finished, and reset it on close (matching afterOpenChange={setSettled}).
  useEffect(() => {
    if (!open) { setSettled(false); return; }
    const t = setTimeout(() => setSettled(true), 260);
    return () => clearTimeout(t);
  }, [open]);
  const f = farmer ?? {};
  const lat = Number(f.latitude), lng = Number(f.longitude);
  const hasPin = f.latitude != null && f.longitude != null && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
  const stateName = f.stateCode ? (STATE_NAME.get(f.stateCode) ?? f.stateCode) : '';
  const place = [f.city, stateName].filter(Boolean).join(', ');
  const wa = String(f.whatsappNumber || f.mobile || '').replace(/[^0-9]/g, '');
  const gmaps = hasPin
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : (place ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}` : null);

  const item = (label: string, value: any) => (value != null && value !== '' ? <Descriptions.Item label={label}>{value}</Descriptions.Item> : null);

  return (
    <Drawer open={open} onClose={onClose} width={460} title="Farmer details">
      {farmer && (
        <>
          <Space align="center" style={{ marginBottom: 12 }}>
            <Avatar size={48} src={f.photoUrl} fallback={<User size={22} />} style={{ background: '#1F8A4C' }} />
            <div>
              <Typography.Title level={5} style={{ margin: 0 }}>{f.fullName}</Typography.Title>
              <Space size={6} style={{ marginTop: 2 }}>
                <Tag tone={STATUS_COLOR[f.status] ?? 'neutral'}>{f.status || 'ACTIVE'}</Tag>
                {f.registeredVia && <Tag>{f.registeredVia}</Tag>}
              </Space>
            </div>
          </Space>

          <Space wrap style={{ marginBottom: 14 }}>
            {f.mobile && <a className={ACTION_BTN} href={`tel:${f.mobile}`}><Phone size={15} /><span>Call</span></a>}
            {wa && <a className={ACTION_BTN} href={`https://wa.me/${wa}`} target="_blank" rel="noopener"><MessageCircle size={15} /><span>WhatsApp</span></a>}
            {gmaps && <a className={ACTION_BTN} href={gmaps} target="_blank" rel="noopener"><MapPin size={15} /><span>Google Maps</span></a>}
          </Space>

          <Descriptions column={1} size="small" bordered>
            {item('Mobile', f.mobile)}
            {item('WhatsApp', f.whatsappNumber)}
            {item('Gender', f.gender)}
            {item('Date of birth', f.dob)}
            {item('Address', f.address)}
            {item('Village / City', f.city)}
            {item('State', stateName)}
            {item('Pincode', f.pincode)}
            {item('Crops', (f.crops ?? []).length ? (f.crops ?? []).map((c: string) => <Tag tone="green" key={c}>{c}</Tag>) : null)}
            {item('Land (acres)', f.landAcres != null ? Number(f.landAcres) : null)}
            {item('Language', f.languagePref ? f.languagePref.toUpperCase() : null)}
            {item('KYC', <Tag tone={f.kycDocUrl ? 'green' : 'neutral'}>{f.kycDocUrl ? 'Yes' : 'No'}</Tag>)}
            {item('Registered', formatIstDate(f.createdAt))}
          </Descriptions>

          <div style={{ marginTop: 16 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Location</Typography.Text>
            {hasPin ? (
              <div style={{ marginTop: 6, height: 260, borderRadius: 10, overflow: 'hidden', border: '1px solid #eee', background: '#eef1eb' }}>
                {settled && (
                  <MapContainer center={[lat, lng]} zoom={13} style={{ height: 260, width: '100%' }} scrollWheelZoom={false}>
                    <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
                    <FixSize />
                    <Marker position={[lat, lng]} icon={PIN} />
                  </MapContainer>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 6, padding: '14px 12px', borderRadius: 10, background: '#f6f8f4', border: '1px solid #eee', textAlign: 'center' }}>
                <MapPin size={20} style={{ color: '#aaa' }} />
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  No GPS pin{place ? <> for <b>{place}</b></> : ''}.
                </div>
                {gmaps && (
                  <a className="mt-1 inline-flex h-8 items-center justify-center gap-1.5 rounded-control px-3 text-[13px] font-semibold leading-none text-content transition-colors duration-150 hover:bg-surface-2" href={gmaps} target="_blank" rel="noopener">
                    <ExternalLink size={14} /><span>Search this area on Google Maps</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </Drawer>
  );
}
